import React, { useCallback, useEffect, useState } from 'react';
import { Download, ExternalLink, FileText, Loader2, RefreshCw, Upload } from 'lucide-react';
import { rfpApi } from '../../services/rfpApi';
import { RFP_FILE_KINDS, RfpFile, RfpFileKind, TEXT_STATUS_STYLE } from '../../types/rfp';

/**
 * 공고 칸의 첨부 목록 — 제안요청서를 받아 텍스트로 뽑은 상태.
 *
 * 상세를 열 때 행이 없으면 `sync` 를 한 번 부른다(담은 날 안에 열면 링크가 살아 있다 — 개찰 뒤엔 죽는다).
 * 실패 · 불가는 이유가 그대로 보인다. 종류는 파일명으로 짐작한 것이라 사람이 고친다.
 */
export const RfpPanel: React.FC<{ projectId: number; hasBid: boolean; onChanged?: () => void }> = ({ projectId, hasBid, onChanged }) => {
  const [files, setFiles] = useState<RfpFile[] | null>(null);
  const [busy, setBusy] = useState<'sync' | 'upload' | number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ file: RfpFile; pages: { pageNo: number; text: string }[] } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await rfpApi.list(projectId);
      if (list.length === 0 && hasBid) {
        setBusy('sync');
        setFiles(await rfpApi.sync(projectId));
      } else setFiles(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : '첨부를 불러오지 못했습니다');
      setFiles([]);
    } finally {
      setBusy(null);
    }
  }, [projectId, hasBid]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy('sync'); setError(null);
    try { setFiles(await rfpApi.sync(projectId)); } catch (e) { setError(e instanceof Error ? e.message : '받지 못했습니다'); } finally { setBusy(null); }
  };
  /* 파일 올리기 — 공고 없이 만든 프로젝트, 또는 공고 첨부에 없던 제안요청서(2026-10-07) */
  const upload = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setBusy('upload'); setError(null);
    try {
      let latest: RfpFile[] | null = null;
      for (const file of Array.from(list)) latest = await rfpApi.upload(projectId, file);
      if (latest) setFiles(latest);
      onChanged?.();
    } catch (e) { setError(e instanceof Error ? e.message : '올리지 못했습니다'); } finally { setBusy(null); }
  };

  const retry = async (f: RfpFile) => {
    setBusy(f.id); setError(null);
    try { const r = await rfpApi.retry(projectId, f.id); setFiles((fs) => (fs ?? []).map((x) => (x.id === r.id ? r : x))); }
    catch (e) { setError(e instanceof Error ? e.message : '다시 받지 못했습니다'); } finally { setBusy(null); }
  };
  const setKind = async (f: RfpFile, kind: RfpFileKind) => {
    try { const r = await rfpApi.setKind(projectId, f.id, kind); setFiles((fs) => (fs ?? []).map((x) => (x.id === r.id ? r : x))); }
    catch (e) { setError(e instanceof Error ? e.message : '종류를 바꾸지 못했습니다'); }
  };
  const open = async (f: RfpFile) => {
    try { setPreview({ file: f, pages: await rfpApi.pages(projectId, f.id, 1, 3) }); }
    catch (e) { setError(e instanceof Error ? e.message : '쪽을 불러오지 못했습니다'); }
  };

  const readable = (files ?? []).filter((f) => f.textStatus === '완료' && (f.fileKind === '제안요청서' || f.fileKind === '과업지시서' || f.fileKind === '평가기준'));

  return (
    <section className="bg-white rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">{hasBid ? '첨부 · 제안요청서' : '첨부 문서'}</h2>
        <span className="text-xs text-slate-400">{hasBid ? '담을 때 받아 둡니다 — 개찰이 지나면 링크가 죽습니다' : '정정공고 · 질의답변처럼 나중에 온 문서를 붙입니다'}</span>
        <label
          title="PDF · HWP · HWPX · DOCX — 글자를 뽑아 배점표 · 초안이 읽습니다"
          className={`ml-auto px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1 ${busy !== null ? 'opacity-40 pointer-events-none' : 'cursor-pointer'}`}
        >
          {busy === 'upload' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {hasBid ? '파일 올리기' : '문서 추가'}
          <input type="file" multiple accept=".pdf,.hwp,.hwpx,.docx" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
        </label>
        {hasBid && (
          <button onClick={sync} disabled={busy !== null} title="스냅샷의 첨부를 다시 확인해 받습니다"
            className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
            {busy === 'sync' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} 첨부 받기
          </button>
        )}
      </div>
      {error && <p role="alert" className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
      <div className="px-4 py-3">
        {files === null ? (
          <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> 불러오는 중...</p>
        ) : files.length === 0 ? (
          <p className="text-sm text-slate-500">{hasBid ? '첨부가 없습니다. 스냅샷에 첨부 목록이 없거나 아직 받지 않았습니다.' : '첨부가 없습니다. 「문서 추가」로 제안요청서 · 과업지시서를 넣으면 배점표 · 초안이 읽습니다.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-slate-100">
                  <th className="text-left font-medium py-2 pr-2">파일</th>
                  <th className="text-left font-medium py-2 pr-2 w-32">종류</th>
                  <th className="text-left font-medium py-2 pr-2 w-20">상태</th>
                  <th className="text-right font-medium py-2 pr-2 w-16">쪽</th>
                  <th className="text-right font-medium py-2 pr-2 w-20">크기</th>
                  <th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.id} className="border-b border-slate-50 align-top">
                    <td className="py-2 pr-2">
                      <div className="text-slate-900 break-all">{f.fileName}</div>
                      {f.textError && <div className="mt-0.5 text-xs text-red-600">{f.textError}</div>}
                    </td>
                    <td className="py-2 pr-2">
                      <select value={f.fileKind} onChange={(e) => setKind(f, e.target.value as RfpFileKind)}
                        className="text-xs border border-transparent rounded px-1 py-0.5 hover:border-slate-200 focus:border-indigo-400 focus:outline-none bg-transparent">
                        {RFP_FILE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${TEXT_STATUS_STYLE[f.textStatus]}`}>{f.textStatus}</span></td>
                    <td className="py-2 pr-2 text-right text-xs text-slate-500 tabular-nums">{f.pageCount ?? '—'}</td>
                    <td className="py-2 pr-2 text-right text-xs text-slate-500 tabular-nums">{f.fileSize ? `${Math.max(1, Math.round(f.fileSize / 1024))}KB` : '—'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {f.textStatus === '완료' && (
                        <button onClick={() => open(f)} title="앞 3쪽 텍스트를 봅니다" className="p-1 rounded text-slate-400 hover:text-indigo-600"><FileText size={13} /></button>
                      )}
                      <button onClick={() => retry(f)} disabled={busy !== null} title="다시 받아서 다시 뽑습니다" className="p-1 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-40">
                        {busy === f.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                      </button>
                      {/* 직접 올린 파일은 원본 주소가 없다(`upload:`) — 링크를 그리지 않는다(#111) */}
                      {!f.sourceUrl.startsWith('upload:') && <a href={f.sourceUrl} target="_blank" rel="noreferrer" title="나라장터 원본" className="inline-block p-1 text-slate-400 hover:text-indigo-600"><ExternalLink size={13} /></a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {files && files.length > 0 && (
          <p className="mt-2 text-xs text-slate-400">
            AI 가 읽는 것은 <b>제안요청서 · 과업지시서 · 평가기준</b> 중 상태가 완료인 것 — 지금 {readable.length}건.
            {files.some((f) => f.textStatus === '불가') && ' 「불가」는 글자를 뽑을 수 없는 형식(엑셀 · 이미지 · zip · 암호/배포용 한글 · 스캔 PDF)입니다. PDF 가 같이 붙어 있으면 그것을 씁니다.'}
          </p>
        )}
        {preview && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{preview.file.fileName} — 앞 {preview.pages.length}쪽</span>
              <button onClick={() => setPreview(null)} className="text-indigo-600 hover:underline">닫기</button>
            </div>
            {preview.pages.map((p) => (
              <pre key={p.pageNo} className="mt-2 whitespace-pre-wrap text-xs text-slate-700 max-h-40 overflow-y-auto"><b>[p.{p.pageNo}]</b> {p.text.slice(0, 1500)}{p.text.length > 1500 ? ' …' : ''}</pre>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
